import React from "react";
import PropTypes from "prop-types";
import { navigate, graphql } from "gatsby";
import { setLanguageCookie } from "../../utils/helper";
import { Link } from "../../sharedComponents/Link";
import NoibuLink, { transformNoibuLink } from "../../sharedComponents/Link/NoibuLink";
import { Footer } from "./Footer";
import { LanguageToggle, languageToggleAdapter } from "../LanguageToggle";
import { RCL } from "../RCL";
import * as style from "./Footer.module.scss";

const FooterConnector = ({ data, footerData, altLangPages, locale }) => {
	const footer = footerData || data.defaultFooter;
	const columns = [];

	const processFooterLinks = columnList => {
		const footerLinksData = [];
		const linkTotal = columnList.items?.length;
		const linkArray = columnList.items?.reduce((accumulator, item) => {
			if (item?.__typename === "ContentfulLink") {
				accumulator.push(
					transformNoibuLink({
						id: item.id,
						url: item.url,
						title: item.linkText,
					})
				);
			}
			return accumulator;
		}, []);
		if (linkTotal === linkArray?.length) {
			footerLinksData.push({
				id: columnList.id,
				title: columnList.title,
				items: linkArray,
			});
		}

		return footerLinksData;
	};

	footer?.linksList?.forEach(list => {
		let footerLinkItems = processFooterLinks(list);

		if (footerLinkItems?.length <= 0) {
			footerLinkItems = list.items.map(menuItem => processFooterLinks(menuItem));

			if (footerLinkItems?.length) {
				footerLinkItems = footerLinkItems.flat();

				// Ignore any nested links or empty menus by filtering them out
				columns.push(footerLinkItems.filter(item => item?.items));
			}
		} else {
			columns.push(...footerLinkItems);
		}
	});

	const transformData = newData =>
		newData
			.filter(x => x)
			.map(item => ({
				image: {
					url: item?.image?.[0]?.secure_url,
					alt: item?.imageAltText,
				},
				link: {
					text: item?.linkText,
					url: item?.url,
					target: item?.target,
				},
			}));
	const flags = transformData([footer.canadianSiteLink, footer.americanSiteLink]);

	const thisYear = new Date().getFullYear();

	const renderLink = link => {
		if (link.isHelpCode) return <NoibuLink title={link.title} />;
		return <Link to={link.url}>{link.title}</Link>;
	};

	return (
		<Footer
			className="page__footer"
			columns={columns}
			renderLinks={renderLink}
			renderCorpsLinks={(link, children) => <Link to={link.url}>{children}</Link>}
			labels={{
				ourSite: <RCL searchKey="our-sites" />,
			}}
			flags={flags}
			locale={locale}
			socialLinks={footer.socialLinks}
			appStoreLinks={footer.appStoreLinks}
			languageToggle={
				altLangPages && (
					<LanguageToggle
						handleChangeLanguage={(lang, path) => {
							setLanguageCookie(lang);
							navigate(path);
						}}
						locale={locale}
						altLangPages={languageToggleAdapter(altLangPages)}
						classNames={{
							currentLanguage: style.langToggleCurrent,
							altLanguage: style.langToggleAlt,
						}}
					/>
				)
			}
			copyrightText={footer.footerCopyright.replace("{{date}}", thisYear)}
		/>
	);
};

FooterConnector.propTypes = {
	data: PropTypes.shape({
		defaultFooter: PropTypes.shape({
			linksList: PropTypes.arrayOf(
				PropTypes.shape({
					items: PropTypes.arrayOf(
						PropTypes.shape({
							id: PropTypes.string,
							linkText: PropTypes.string,
							target: PropTypes.string,
							url: PropTypes.string,
						})
					),
				})
			),
			footerCopyright: PropTypes.string,
			americanSiteLink: PropTypes.shape({
				id: PropTypes.string,
				image: PropTypes.arrayOf(
					PropTypes.shape({
						secure_url: PropTypes.string,
					})
				),
				imageAltText: PropTypes.string,
				target: PropTypes.string,
				url: PropTypes.string,
			}),
			canadianSiteLink: PropTypes.shape({
				id: PropTypes.string,
				image: PropTypes.arrayOf(
					PropTypes.shape({
						secure_url: PropTypes.string,
					})
				),
				imageAltText: PropTypes.string,
				target: PropTypes.string,
				url: PropTypes.string,
			}),
			socialLinks: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.string,
					link: PropTypes.shape({
						url: PropTypes.string,
						linkText: PropTypes.string,
						target: PropTypes.string,
					}),
					iconName: PropTypes.string,
				})
			),
			appStoreLinks: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.string,
					link: PropTypes.shape({
						url: PropTypes.string,
						linkText: PropTypes.string,
						target: PropTypes.string,
					}),
					iconName: PropTypes.string,
				})
			),
		}),
	}).isRequired,
	footerData: PropTypes.shape({
		linksList: PropTypes.arrayOf(
			PropTypes.shape({
				items: PropTypes.arrayOf(
					PropTypes.shape({
						id: PropTypes.string,
						linkText: PropTypes.string,
						target: PropTypes.string,
						url: PropTypes.string,
					})
				),
			})
		),
		footerCopyright: PropTypes.string,
		americanSiteLink: PropTypes.shape({
			id: PropTypes.string,
			image: PropTypes.arrayOf(
				PropTypes.shape({
					secure_url: PropTypes.string,
				})
			),
			imageAltText: PropTypes.string,
			target: PropTypes.string,
			url: PropTypes.string,
		}),
		canadianSiteLink: PropTypes.shape({
			id: PropTypes.string,
			image: PropTypes.arrayOf(
				PropTypes.shape({
					secure_url: PropTypes.string,
				})
			),
			imageAltText: PropTypes.string,
			target: PropTypes.string,
			url: PropTypes.string,
		}),
		socialLinks: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.string,
				link: PropTypes.shape({
					url: PropTypes.string,
					linkText: PropTypes.string,
					target: PropTypes.string,
				}),
				iconName: PropTypes.string,
			})
		),
		appStoreLinks: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.string,
				link: PropTypes.shape({
					url: PropTypes.string,
					linkText: PropTypes.string,
					target: PropTypes.string,
				}),
				iconName: PropTypes.string,
			})
		),
	}),
	altLangPages: PropTypes.shape({
		edges: PropTypes.arrayOf(
			PropTypes.shape({
				node: PropTypes.shape({
					context: PropTypes.shape({
						id: PropTypes.string,
						locale: PropTypes.string,
					}),
					path: PropTypes.string,
				}),
			})
		),
	}).isRequired,
	locale: PropTypes.string.isRequired,
};

FooterConnector.defaultProps = {
	footerData: undefined,
};

export default FooterConnector;
export { FooterConnector };

export const query = graphql`
	query GetDefaultLocalizedFooter($locale: String!) {
		defaultFooter: contentfulFooter(
			contentful_id: { eq: "3ah2Hoi1KtsePcyyjFxC5K" }
			node_locale: { eq: $locale }
		) {
			...footer
		}
	}
`;
